import type { HubConnection } from '@microsoft/signalr'
import type {
  ChannelEvent,
  ForceResyncEvent,
  PersonalEvent,
  PresenceEvent,
  MemberQuietEvent,
  ReadUpdatedEvent,
  TypingEvent,
} from '../api/types'
import { db, deleteChannelData, channelTables } from '../db/db'
import { useEphemeral } from '../state/ephemeral'
import { currentMeId } from '../state/session'
import { soundForDeviceRequested, soundForLiveEvent } from '../sound/notify'
import { forceResync, receiveEvent, syncAll } from '../sync/syncEngine'

export interface HandlerDeps {
  onSessionRevoked: () => void
  onSettingsChanged: () => void
  /** device.requested / approved / denied / revoked */
  onDeviceEvent: (event: PersonalEvent) => void
}

/** Local read marker for the current user (another device read, or our own MarkRead echo). */
export async function applyOwnRead(channelId: string, seq: number): Promise<void> {
  const meId = currentMeId()
  await db.transaction('rw', [db.channels, db.messages], async () => {
    const channel = await db.channels.get(channelId)
    if (!channel || seq <= channel.lastReadSeq) return
    let unread = 0
    if (seq < channel.headSeq) {
      unread = await db.messages
        .where('[channelId+seq]')
        .between([channelId, seq + 1], [channelId, Number.MAX_SAFE_INTEGER], true, true)
        .filter((m) => m.authorId !== meId && !m.deletedAt)
        .count()
    }
    await db.channels.update(channelId, { lastReadSeq: seq, unread: Math.min(unread, channel.unread) })
  })
}

export function registerHandlers(connection: HubConnection, deps: HandlerDeps): void {
  connection.on('ChannelEvent', (event: ChannelEvent) => {
    if (event.type === 'message.created') {
      useEphemeral.getState().clearTyping(event.channelId, event.payload.message.authorId)
    }
    // Sounds only for events that arrived live and were new on this device (not duplicates of our own sends).
    void receiveEvent(event).then((outcomes) => {
      if (outcomes[0] === 'applied') void soundForLiveEvent(event)
    })
  })

  connection.on('PersonalEvent', (event: PersonalEvent) => {
    switch (event.type) {
      case 'channel.added':
        void syncAll()
        break
      case 'channel.removed':
        if (event.channelId) {
          const id = event.channelId
          void db.transaction('rw', channelTables(), () => deleteChannelData(id))
        }
        break
      case 'session.revoked':
        deps.onSessionRevoked()
        break
      case 'settings.changed':
        deps.onSettingsChanged()
        break
      case 'device.requested':
        void soundForDeviceRequested()
        deps.onDeviceEvent(event)
        break
      case 'device.approved':
      case 'device.denied':
      case 'device.revoked':
        deps.onDeviceEvent(event)
        break
    }
  })

  connection.on('Presence', (event: PresenceEvent) => {
    useEphemeral.getState().setPresence(event.userId, event.online)
  })

  connection.on('Typing', (event: TypingEvent) => {
    if (event.userId === currentMeId()) return
    useEphemeral.getState().setTyping(event.channelId, event.userId)
  })

  connection.on('ReadUpdated', (event: ReadUpdatedEvent) => {
    if (event.userId === currentMeId()) void applyOwnRead(event.channelId, event.seq)
    else useEphemeral.getState().setRead(event.channelId, event.userId, event.seq)
  })

  // Someone's notifications for a conversation changed: everyone in it is shown this, so it travels like a read receipt.
  connection.on('MemberQuiet', (event: MemberQuietEvent) => {
    void db.members.update([event.channelId, event.userId], { quiet: event.quiet })
    if (event.userId !== currentMeId()) {
      void db.channels.get(event.channelId).then((channel) => {
        if (channel?.type === 'dm') void db.channels.update(event.channelId, { partnerQuiet: event.quiet })
      })
    }
  })

  connection.on('ForceResync', (event: ForceResyncEvent) => {
    void forceResync(event.channelId)
  })
}
